<template>
    <div class="connection-setup">
        <div class="setup-header">
            <h4>Configure Connection</h4>
            <p class="text-muted">Enter property ID, select user role and create token name to connect other systems</p>
        </div>

        <!-- Selected Branch & Merchant Info -->
        <div class="info-card">
            <div class="info-header">
                <i class="fas fa-info-circle"></i>
                <span>Selected Configuration</span>
            </div>
            <div class="info-body">
                <div class="info-row">
                    <span class="label">Branch:</span>
                    <span class="value">{{ branchInfo }}</span>
                </div>
                <div class="info-row">
                    <span class="label">Merchant:</span>
                    <span class="value">{{ merchantInfo }}</span>
                </div>
            </div>
        </div>

        <!-- Connection Form -->
        <div class="connection-form">
            <div class="row">
                <div class="col-md-6">
                    <div class="form-group">
                        <label for="property-id">
                            Property ID <span class="required">*</span>
                            <i class="fas fa-info-circle info-icon" title="Numeric ID from other System/Application"></i>
                        </label>
                        <input id="property-id" type="text" inputmode="numeric" pattern="[0-9]*" maxlength="20"
                            v-model="formData.property_id" class="form-control"
                            placeholder="Enter property ID (e.g., 1234)" @input="onPropertyIdChange" />
                    </div>
                </div>

                <div class="col-md-6">
                    <div class="form-group">
                        <label for="service-account-role">
                            User Role for Connection<span class="required">*</span>
                            <i class="fas fa-info-circle info-icon" title="Role for the token authority to generate Personal Access Token"></i>
                        </label>
                        <select id="service-account-role" v-model="formData.service_account_role" 
                            class="form-control" :disabled="roles.length === 0">
                            <option value="" v-if="roles.length === 0">No roles available - Please setup roles first</option>
                            <option value="" v-else>-- Select Role --</option>
                            <option v-for="role in roles" :key="role.value" :value="role.text">
                                {{ role.text }}
                            </option>
                        </select>
                        <!-- High Privilege Role Warning -->
                        <div v-if="isHighPrivilegeRole" class="alert alert-warning role-warning">
                            <i class="fas fa-exclamation-triangle"></i>
                            <span>The role you selected has extensive access permissions, please ensure this role matches your requirements!</span>
                        </div>
                        <!-- No Roles Available Info -->
                        <div v-if="roles.length === 0" class="alert alert-info role-info">
                            <i class="fas fa-info-circle"></i>
                            <span>No roles found. Please setup user roles in your corporate settings before creating a connection.</span>
                        </div>
                    </div>
                </div>
            </div>

            <div class="form-group">
                <label for="token-name">
                    Token Name <span class="required">*</span>
                    <i class="fas fa-info-circle info-icon" title="Identifier for this Personal Access Token connection"></i>
                </label>
                <input id="token-name" type="text" v-model="formData.token_name" class="form-control"
                    placeholder="Enter token name" />
            </div>

            <!-- Info Alert -->
            <div class="alert alert-info">
                <i class="fas fa-lightbulb"></i>
                <div>
                    <strong>Auto-Generated Resources:</strong>
                    <ul>
                        <li>A account of token authority will be automatically created for this connection</li>
                        <li>An access token will be generated for API authentication from token authority</li>
                        <li>A product will be automatically created for this connection</li>
                        <li>Authentication URL will be configured automatically</li>
                    </ul>
                </div>
            </div>
        </div>

        <div v-if="validationError" class="alert alert-danger mt-3">
            {{ validationError }}
        </div>
    </div>
</template>

<script>
import { ref, computed, watch, onMounted } from 'vue'
import { useStore } from 'vuex'

export default {
    name: 'ConnectionSetup',
    props: {
        modelValue: {
            type: Object,
            default: () => ({})
        },
        branchMerchantData: {
            type: Object,
            default: () => ({})
        }
    },
    emits: ['update:modelValue', 'valid'],
    setup(props, { emit }) {
        const validationError = ref('')

        const formData = ref({
            property_id: '',
            token_name: '',
            service_account_role: ''
        })

        const store = useStore()
        const availableBranches = computed(() => store.getters['ConnectionApps/branches'])
        const roles = ref([])

        /**
         * Check if selected role is a high-privilege role
         * High-privilege roles: root, corporate, branch, unit
         * @returns {boolean} True if selected role has extensive permissions
         */
        const isHighPrivilegeRole = computed(() => {
            if (!formData.value.service_account_role) return false
            
            const highPrivilegeRoles = ['root', 'corporate', 'branch', 'unit']
            const selectedRole = formData.value.service_account_role.toLowerCase()
            
            return highPrivilegeRoles.some(role => selectedRole.includes(role))
        })

        /**
         * Get branch information from the selected branch ID
         * Returns an object with branch details or empty object if not found
         * 
         * @returns {Object} Branch information
         */
        const branchInfo = computed(() => {
            if (props.branchMerchantData.setup_type === 'existing') {
                const branch = availableBranches.value.find(b => b.branch_id === props.branchMerchantData.branch_id)
                return branch ? `${branch.branch_name} (${branch.branch_code})` : props.branchMerchantData.branch_id
            } else {
                const b = props.branchMerchantData.branch
                return b ? `${b.name} (${b.code})` : 'New Branch'
            }
        })

        /**
         * Get merchant information from the selected merchant ID
         * Returns an object with merchant details or empty object if not found
         * 
         * @returns {Object} Merchant information
         */
        const merchantInfo = computed(() => {
            if (props.branchMerchantData.setup_type === 'existing') {
                const branch = availableBranches.value.find(b => b.branch_id === props.branchMerchantData.branch_id)
                if (branch && branch.merchants) {
                    const merchant = branch.merchants.find(m => m.merchant_id === props.branchMerchantData.merchant_id)
                    return merchant ? `${merchant.merchant_name} (${merchant.merchant_code})` : props.branchMerchantData.merchant_id
                }
                return props.branchMerchantData.merchant_id
            } else {
                const m = props.branchMerchantData.merchant
                return m ? `${m.name} (${m.code})` : 'New Merchant'
            }
        })

        watch(formData, () => {
            emitUpdate()
        }, { deep: true })

        /**
         * Handles input changes for the property ID field.
         * Sanitizes input to allow only numbers and auto-generates token name.
         * 
         * @param {Event} event The input event object.
         */
        function onPropertyIdChange(event) {
            if (event && event.target) {
                const value = event.target.value
                const numericValue = value.replace(/[^0-9]/g, '')

                if (value !== numericValue) {
                    formData.value.property_id = numericValue
                    event.target.value = numericValue
                }
            }

            if (!formData.value.token_name || formData.value.token_name.startsWith('Connection Token')) {
                let branchName = ''
                if (props.branchMerchantData.setup_type === 'existing') {
                    const branch = availableBranches.value.find(b => b.branch_id === props.branchMerchantData.branch_id)
                    branchName = branch ? branch.branch_name : ''
                } else {
                    branchName = props.branchMerchantData.branch?.name || ''
                }

                formData.value.token_name = `Connection Token - ${branchName} - Property ${formData.value.property_id}`
            }

            emitUpdate()
        }

        function emitUpdate() {
            emit('update:modelValue', formData.value)
            validateForm()
        }

        function validateForm() {
            validationError.value = ''

            if (!formData.value.property_id) {
                validationError.value = 'Property ID is required'
                emit('valid', false)
                return false
            }

            if (!/^\d+$/.test(formData.value.property_id)) {
                validationError.value = 'Property ID must be numeric'
                emit('valid', false)
                return false
            }

            if (!formData.value.token_name) {
                validationError.value = 'Token name is required'
                emit('valid', false)
                return false
            }

            if (formData.value.token_name.length < 3) {
                validationError.value = 'Token name must be at least 3 characters'
                emit('valid', false)
                return false
            }

            if (!formData.value.service_account_role) {
                validationError.value = 'Token authority role is required'
                emit('valid', false)
                return false
            }

            emit('valid', true)
            return true
        }

        /**
         * Load available roles for the corporate
         * Fetches roles from the store and formats them for dropdown
         */
        async function loadRoles() {
            try {
                const { corporate_id } = await store.dispatch('Auth/getBusinessProfile')
                await store.dispatch('Role/read_all', corporate_id)
                roles.value = store.state.Role.roles.map(entity => ({ value: entity.id, text: entity.name }))
                // Auto-select appropriate role for connection - Priority: merchant > connectivity > connection
                if (roles.value.length > 0 && !formData.value.service_account_role) {
                    const preferredRolePatterns = ['merchant', 'connectivity', 'connection']
                    
                    for (const pattern of preferredRolePatterns) {
                        const matchedRole = roles.value.find(role => 
                            role.text.toLowerCase().includes(pattern)
                        )
                        
                        if (matchedRole) {
                            formData.value.service_account_role = matchedRole.text
                            break
                        }
                    }
                }
            } catch (err) {
                console.error('Error loading roles:', err)
                validationError.value = 'Failed to load roles'
            }
        }

        onMounted(async () => {
            await loadRoles()
            if (props.modelValue && Object.keys(props.modelValue).length > 0) {
                formData.value = { ...formData.value, ...props.modelValue }
            }
        })

        return {
            formData,
            validationError,
            branchInfo,
            merchantInfo,
            onPropertyIdChange,
            roles,
            isHighPrivilegeRole
        }
    }
}
</script>

<style scoped>
.connection-setup {
    padding: 1.5rem;
}

.setup-header {
    margin-bottom: 2rem;
}

.setup-header h4 {
    margin-bottom: 0.5rem;
    color: #111827;
}

.text-muted {
    color: #6b7280;
    font-size: 0.875rem;
}

.info-card {
    background: color-mix(in srgb, var(--primary-color), transparent 95%);
    border: 1px solid color-mix(in srgb, var(--primary-color), transparent 85%);
    border-radius: 12px;
    margin-bottom: 2rem;
    overflow: hidden;
}

.info-header {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.875rem 1.25rem;
    background: color-mix(in srgb, var(--primary-color), transparent 90%);
    border-bottom: 1px solid color-mix(in srgb, var(--primary-color), transparent 85%);
    font-weight: 600;
    color: var(--primary-color, #F93E3E);
}

.info-header i {
    color: var(--primary-color, #F93E3E);
}

.info-body {
    padding: 1.25rem;
}

.info-row {
    display: flex;
    justify-content: space-between;
    padding: 0.5rem 0;
}

.info-row .label {
    color: #64748b;
    font-weight: 500;
    font-size: 0.875rem;
}

.info-row .value {
    color: #0f172a;
    font-weight: 600;
    font-size: 0.875rem;
}

.connection-form {
    background: #f9fafb;
    padding: 1.5rem;
    border-radius: 12px;
}

.form-group {
    margin-bottom: 1.5rem;
}

.form-group label {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 500;
    color: #374151;
    font-size: 0.875rem;
}

.help-text {
    font-weight: 400;
    color: #9ca3af;
    font-size: 0.8125rem;
}

.required {
    color: #dc2626;
}

.info-icon {
    color: #6b7280;
    font-size: 0.875rem;
    margin-left: 0.25rem;
    cursor: help;
    transition: color 0.2s;
}

.info-icon:hover {
    color: var(--primary-color, #F93E3E);
}

.form-control {
    width: 100%;
    padding: 0.75rem 1rem;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    font-size: 0.875rem;
    transition: all 0.2s ease;
}

.form-control:focus {
    outline: none;
    border-color: var(--primary-color, #F93E3E);
    box-shadow: 0 0 0 3px color-mix(in srgb, var(--primary-color), transparent 85%);
}

/* Input with feedback wrapper */
.input-with-feedback {
    position: relative;
}

.input-with-feedback .form-control {
    padding-right: 2.5rem;
}

.input-feedback-icon {
    position: absolute;
    right: 0.75rem;
    top: 50%;
    transform: translateY(-50%);
    pointer-events: none;
}

.input-feedback-icon i {
    font-size: 1.125rem;
}

.text-primary {
    color: var(--primary-color, #F93E3E);
}

.text-success {
    color: #10b981;
}

.text-danger {
    color: #dc2626;
}

/* Validation states */
.form-control.input-checking {
    border-color: var(--primary-color, #F93E3E);
}

.form-control.input-valid {
    border-color: #10b981;
}

.form-control.input-invalid {
    border-color: #dc2626;
}

.alert {
    padding: 1rem;
    border-radius: 8px;
    font-size: 0.875rem;
}

.alert-info {
    background: color-mix(in srgb, var(--primary-color), transparent 95%);
    color: var(--primary-color, #F93E3E);
    border: 1px solid color-mix(in srgb, var(--primary-color), transparent 80%);
    display: flex;
    gap: 0.75rem;
}

.alert-info i {
    color: var(--primary-color, #F93E3E);
    margin-top: 0.125rem;
}

.alert-info strong {
    display: block;
    margin-bottom: 0.5rem;
    color: var(--primary-color, #F93E3E);
}

.alert-info ul {
    margin: 0;
    padding-left: 1.25rem;
}

.alert-info li {
    margin-bottom: 0.25rem;
}

.alert-warning {
    background: #fef3c7;
    border: 1px solid #fbbf24;
    color: #92400e;
}

.role-warning {
    margin-top: 0.75rem;
    padding: 0.75rem 1rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.875rem;
    line-height: 1.4;
}

.role-warning i {
    color: #f59e0b;
    font-size: 1rem;
    flex-shrink: 0;
}

.role-warning span {
    flex: 1;
}

.role-info {
    margin-top: 0.75rem;
    padding: 0.75rem 1rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.875rem;
    line-height: 1.4;
}

.role-info i {
    color: #3b82f6;
    font-size: 1rem;
    flex-shrink: 0;
}

.role-info span {
    flex: 1;
}

.alert-danger {
    background: #fee2e2;
    color: #991b1b;
    border: 1px solid #fecaca;
}
</style>
